import React, { useMemo, useRef, useState, useEffect } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedVersionAtom,
    jobOpenCommitmentsAtom,
    jobCostDetailAtom,
    jobUserDataAtom,
    jobPhaseCodesAtom,
    sideMenuBudgetsAtom,
    sideMenuCostsAtom,
} from '../store/company';
import { pNum } from '../db/company';
import { buildEtcLedger } from '../helpers/etcLedger';
import { loadResourceCodeTables } from '../helpers/resourceCodeDb';

// PSR export slide: "3rd-Party Cost Drivers".
// Two panels, themed to match the other PSR slides (white card, navy headers):
//   1. Top 3rd-Party Cost Drivers / Watch Items  - top phase codes by EAC $
//   2. Budget vs EAC by 3rd-Party Type           - Materials / Subs summary + bars
//
// Data sources (all live, per the selected job/version):
//   - Budget / EAC per type : sideMenuBudgetsAtom + sideMenuCostsAtom (the exact
//                             values the side menu shows, so the slide can't drift
//                             from the rest of the app).
//   - Committed / Received / Open : jobOpenCommitmentsAtom (open PO lines).
//   - Actual (JTD) : jobCostDetailAtom (posted Job_Cost).
//
// Cost-type classification matches bidForecast.js:
//   Materials = 53000-53999, Subcontractors = 54000-54999.

// Match the PSR CO Log slide palette (cologPSRView.js).
const COLORS = {
    navy: '#173659',
    navyDark: '#0F2A4A',
    headerText: '#FFFFFF',
    subHeader: '#224774',
    grid: '#B7C4D3',
    zebra: '#F6F8FB',
    cellText: '#0E1B2B',
    muted: '#5A6B7E',
    barOutline: '#9CA3AF',
    white: '#FFFFFF',
    budgetBar: '#2F5C8F',
    eacBar: '#E08600',
    over: '#C0392B',
    under: '#1E7E34',
};

const inRange = (code, lo, hi) => {
    const c = (code || '') + '';
    return c >= lo && c <= hi;
};
const isMaterial = (code) => inRange(code, '53000', '53999');
const isSub = (code) => inRange(code, '54000', '54999');

// Open / committed amount per PO line, mirroring bidForecast's committed calc.
const lineOpen = (r) => pNum(r.PO_True_Open_Amount);
// Full committed value of the PO line (ordered). Falls back to open+received.
const lineOrdered = (r) => {
    const ordered = pNum(r.PO_Ordered_Amount);
    if (ordered) return ordered;
    return pNum(r.PO_True_Open_Amount) + pNum(r.PO_Received_Amount);
};

// Amounts shown in thousands with comma separators, e.g. 6,100,000 -> "$6,100K".
const fK = (v) => {
    const n = pNum(v) || 0;
    const k = Math.round(n / 1000);
    return (k < 0 ? '-$' : '$') + Math.abs(k).toLocaleString('en-US') + 'K';
};
const fPct = (v) => (isFinite(v) ? Math.round(v * 100) : 0) + '%';

const ThirdPartyCostDrivers = (a) => {
    const ref = useRef();
    if (a?.photo && a.setJobRef) {
        a.setJobRef(a.job, 'thirdParty', ref);
    }

    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [costDetail] = useAtom(jobCostDetailAtom);
    const [userData] = useAtom(jobUserDataAtom);
    const [phaseDescriptions] = useAtom(jobPhaseCodesAtom);
    const [budgets] = useAtom(sideMenuBudgetsAtom);
    const [costs] = useAtom(sideMenuCostsAtom);

    // Watch Items table can be viewed grouped by Phase Code or by Resource Code.
    const [groupBy, setGroupBy] = useState('phase');
    const [dbResourceCodes, setDbResourceCodes] = useState({ byCode: {} });
    useEffect(() => {
        if (!selectedVersion) return;
        loadResourceCodeTables({ forecastGuid: selectedVersion }).then(setDbResourceCodes);
    }, [selectedVersion]);

    // Phase × Cost Type and Resource × Cost Type budget matrices (Day Zero Budget
    // + approved Change Orders), plus the raw forecast ETC entries — all built the
    // same way the other third-party pages do, needed so Watch Items' Budget/ETC/
    // EAC agree with the Materials/Subs/All Third Party pages instead of being
    // derived only from actuals + open PO commitments.
    const { budgetByPhaseCt, budgetByResourceCt, forecastEntries } = useMemo(() => {
        const ledger = buildEtcLedger(userData || [], 'All Third Party', ['Phase_Code', 'Cost_Type_Code'], {});
        return {
            budgetByPhaseCt: ledger.budgetByPhaseCt,
            budgetByResourceCt: ledger.budgetByResourceCt,
            forecastEntries: ledger.allEntries,
        };
    }, [userData]);

    // ---- Type-level rollup (Materials / Subs) -----------------------------
    const typeRows = useMemo(() => {
        // Actual cost-to-date (received / progressed & invoiced) by group.
        let matJtd = 0, subJtd = 0;
        (costDetail || []).forEach((r) => {
            const jc = pNum(r.Job_Cost);
            if (isMaterial(r.Cost_Type_Code)) matJtd += jc;
            else if (isSub(r.Cost_Type_Code)) subJtd += jc;
        });
        // Open commitments (ordered but not yet received) by group.
        let matOpen = 0, subOpen = 0;
        (openCommitments || []).forEach((r) => {
            const o = lineOpen(r);
            if (isMaterial(r.Cost_Type_Code)) matOpen += o;
            else if (isSub(r.Cost_Type_Code)) subOpen += o;
        });

        const build = (label, budget, eac, jtd, open) => {
            const committed = jtd + open;            // spent + still on PO
            const etc = eac - jtd;                   // estimate to complete: EAC less cost to date
            const eacVar = eac - budget;
            const pctReceived = committed > 0 ? jtd / committed : 0;
            return { label, budget, actuals: jtd, committed, open, etc, eac, eacVar, pctReceived };
        };

        const materials = build(
            'Materials',
            pNum(budgets?.materialsDayZeroBudget),
            pNum(costs?.materials),
            matJtd, matOpen
        );
        const subs = build(
            'Subs',
            pNum(budgets?.subcontractorsDayZeroBudget),
            pNum(costs?.subs),
            subJtd, subOpen
        );
        const total = {
            label: 'Total',
            budget: materials.budget + subs.budget,
            actuals: materials.actuals + subs.actuals,
            committed: materials.committed + subs.committed,
            open: materials.open + subs.open,
            etc: materials.etc + subs.etc,
            eac: materials.eac + subs.eac,
            eacVar: materials.eacVar + subs.eacVar,
            pctReceived: (materials.committed + subs.committed) > 0
                ? (matJtd + subJtd) / (materials.committed + subs.committed)
                : 0,
        };
        return { materials, subs, total };
    }, [openCommitments, costDetail, budgets, costs]);

    // ---- Watch items (top phase/resource codes by EAC $) ------------------
    const watchItems = useMemo(() => {
        const norm = (s) => (s || '').trim();

        // Resolve the grouping key + label for a PO/cost row, per the active toggle.
        const keyOf = groupBy === 'resource'
            ? (r) => {
                const code = norm(r.Resource_Code || r.Purchasing_Reporting_Code_4 || r.Purchasing_Reporting_Code_1) || 'No Resource';
                const desc = dbResourceCodes.byCode?.[code]?.description
                    || norm(r.Purchasing_Reporting_Code_4_Description)
                    || norm(r.Purchasing_Reporting_Code_1_Description)
                    || '';
                return { code, desc };
            }
            : (r) => {
                const code = norm(r.Phase_Code) || 'No Phase';
                // phaseDescriptions is the app-wide phase lookup, so a group seeded
                // purely from a forecast ETC entry (no OC/CH row) still gets a label.
                const desc = phaseDescriptions?.[code] || norm(r.Phase_Code_Description) || r.Cost_Type_Description || '';
                return { code, desc };
            };

        const budgetMatrix = groupBy === 'resource' ? budgetByResourceCt : budgetByPhaseCt;

        // Budget for a group, restricted to the cost types in its type (Mat./Sub).
        const budgetForGroupType = (code, isMat) => {
            const cts = budgetMatrix[code] || {};
            let total = 0;
            Object.entries(cts).forEach(([ct, amt]) => {
                if (isMat ? isMaterial(ct) : isSub(ct)) total += pNum(amt);
            });
            return total;
        };

        // Seed/fetch a group's row, from either source below — a group must show
        // up here even if it has actuals but no open commitment (fully closed
        // out), so the top-10-by-EAC pick below sees every cost driver, not just
        // ones with an open PO line.
        const byGroup = {};
        const ensureGroup = (code, type, desc) => {
            const key = code + '|' + type;
            if (!byGroup[key]) {
                byGroup[key] = {
                    key,
                    code,
                    type,
                    desc,
                    committed: 0,   // total ordered on PO
                    open: 0,        // PO balance not yet received
                };
            } else if (!byGroup[key].desc && desc) {
                byGroup[key].desc = desc;
            }
            return key;
        };

        // Actual cost-to-date per group + type (Materials/Subs only). A group
        // must show up here even with no open commitment (fully closed out) or
        // no actuals yet (pure forecast), so the top-10-by-EAC pick below sees
        // every cost driver, not just ones with actual/open PO activity.
        const actualsByGroup = {};
        (costDetail || []).forEach((r) => {
            const mat = isMaterial(r.Cost_Type_Code);
            const sub = isSub(r.Cost_Type_Code);
            if (!mat && !sub) return;
            const { code, desc } = keyOf(r);
            const key = ensureGroup(code, mat ? 'Mat.' : 'Sub', desc);
            actualsByGroup[key] = (actualsByGroup[key] || 0) + pNum(r.Job_Cost);
        });

        (openCommitments || []).forEach((r) => {
            const mat = isMaterial(r.Cost_Type_Code);
            const sub = isSub(r.Cost_Type_Code);
            if (!mat && !sub) return;
            const { code, desc } = keyOf(r);
            const key = ensureGroup(code, mat ? 'Mat.' : 'Sub', desc);
            byGroup[key].committed += lineOrdered(r);
            byGroup[key].open += lineOpen(r);
        });

        // ETC = the dollar amount users actually forecasted (Materials/Subs/
        // Rentals/Other tabs in Forecast_User_Data), same source the Materials
        // and All Third Party pages read — not a derived leftover. A phase or
        // resource can carry real EAC from this alone, with no actual/open yet.
        const etcByGroup = {};
        (forecastEntries || []).forEach((e) => {
            const mat = isMaterial(e.Cost_Type_Code);
            const sub = isSub(e.Cost_Type_Code);
            if (!mat && !sub) return;
            const { code, desc } = keyOf(e);
            const key = ensureGroup(code, mat ? 'Mat.' : 'Sub', desc);
            etcByGroup[key] = (etcByGroup[key] || 0) + pNum(e.amount);
        });

        const all = Object.values(byGroup)
            .map((v) => {
                const actuals = actualsByGroup[v.key] || 0;
                const etc = etcByGroup[v.key] || 0;
                // EAC = actuals + open PO balance + forecasted ETC, matching the
                // Total column on the Materials/All Third Party pivot tables.
                const eac = actuals + v.open + etc;
                const budget = budgetForGroupType(v.code, v.type === 'Mat.');
                const progressBase = actuals + v.open;
                return {
                    ...v,
                    actuals,
                    etc,
                    eac,
                    budget,
                    eacVar: eac - budget,
                    pctReceived: progressBase > 0 ? actuals / progressBase : 0,
                };
            });

        // Materials block then Subs block, each independently sorted by EAC
        // descending — split 5/5 by default, but always fill up to 10 total: if
        // one type has fewer than 5 records, the other type takes the leftover
        // slots (e.g. Sub has only 4 → Mat shows 6).
        const matAll = all.filter((v) => v.type === 'Mat.').sort((x, y) => y.eac - x.eac);
        const subAll = all.filter((v) => v.type === 'Sub').sort((x, y) => y.eac - x.eac);
        let matTake = Math.min(5, matAll.length);
        let subTake = Math.min(5, subAll.length);
        let leftover = 10 - matTake - subTake;
        if (leftover > 0) {
            const matExtra = Math.min(leftover, matAll.length - matTake);
            matTake += matExtra;
            leftover -= matExtra;
            const subExtra = Math.min(leftover, subAll.length - subTake);
            subTake += subExtra;
        }
        const items = [...matAll.slice(0, matTake), ...subAll.slice(0, subTake)];
        return { items, matShown: matTake, matTotal: matAll.length, subShown: subTake, subTotal: subAll.length };
    }, [openCommitments, costDetail, forecastEntries, budgetByPhaseCt, budgetByResourceCt, groupBy, dbResourceCodes, phaseDescriptions]);

    // ---- shared cell styles ------------------------------------------------
    // Sized to hold 8-9 columns per table (incl. Budget/Actuals/Variance)
    // without growing either table wider than the fixed-width card.
    const th2 = (w, align = 'right') => ({
        width: w, paddingTop: 7, paddingBottom: 7, paddingLeft: 6, paddingRight: 6,
        color: COLORS.headerText, fontWeight: '700', fontSize: 11.5, textAlign: align,
    });
    const td2 = (w, align = 'right', bold = false) => ({
        width: w, paddingTop: 6, paddingBottom: 6, paddingLeft: 6, paddingRight: 6,
        color: COLORS.cellText, fontWeight: bold ? '700' : '500', fontSize: 12,
        textAlign: align,
    });
    // Color by the rounded-to-$K value actually shown, not the raw cents/dollars —
    // otherwise a variance that displays as "$0K" can still render red/green from
    // a sub-$500 raw difference that rounded away.
    const varColor = (v) => {
        const k = Math.round(pNum(v) / 1000);
        return k > 0 ? COLORS.over : k < 0 ? COLORS.under : COLORS.cellText;
    };
    // Color alone (red = over budget, green = under) conveys direction, so the
    // variance amount itself is shown as a plain magnitude with no +/- sign.
    const fVar = (v) => fK(Math.abs(v));

    const SectionTitle = ({ children }) => (
        <Text style={{
            fontSize: 15, fontWeight: '700', letterSpacing: 0.5,
            color: COLORS.navy, marginBottom: 8, textTransform: 'uppercase',
        }}>{children}</Text>
    );

    return (
        <View style={{ flex: 1, backgroundColor: COLORS.white }}>
            <View ref={ref} style={{
                width: 1180, backgroundColor: COLORS.white, borderRadius: 6,
                padding: 24, margin: 30, alignSelf: 'flex-start',
                shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1, shadowRadius: 4, elevation: 3,
            }}>
                {/* ---------- Panel 1: Watch items ---------- */}
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                        <Text style={{ fontSize: 15, fontWeight: '700', letterSpacing: 0.5, color: COLORS.navy, textTransform: 'uppercase' }}>
                            3rd-Party Cost Drivers / Watch Items
                        </Text>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: COLORS.muted, marginLeft: 10 }}>
                            Mat. {watchItems.matShown} / {watchItems.matTotal} · Sub {watchItems.subShown} / {watchItems.subTotal} {groupBy === 'resource' ? 'resources' : 'phase codes'}
                        </Text>
                    </View>
                    <View style={{ flexDirection: 'row', borderWidth: 1, borderColor: COLORS.subHeader, borderRadius: 4, overflow: 'hidden' }}>
                        {[['phase', 'By Phase'], ['resource', 'By Resource']].map(([opt, label]) => (
                            <Pressable key={opt} onPress={() => setGroupBy(opt)} style={{
                                paddingTop: 5, paddingBottom: 5, paddingLeft: 12, paddingRight: 12,
                                backgroundColor: groupBy === opt ? COLORS.subHeader : COLORS.white,
                            }}>
                                <Text style={{ fontSize: 11, fontWeight: '700', color: groupBy === opt ? COLORS.headerText : COLORS.navy }}>
                                    {label}
                                </Text>
                            </Pressable>
                        ))}
                    </View>
                </View>
                <View style={{ borderWidth: 1, borderColor: COLORS.grid, borderRadius: 4, overflow: 'hidden' }}>
                    <View style={{ flexDirection: 'row', backgroundColor: COLORS.navyDark }}>
                        <Text style={th2(250, 'left')}>{groupBy === 'resource' ? 'Resource' : 'Phase Code'}</Text>
                        <Text style={th2(55, 'center')}>Type</Text>
                        <Text style={th2(118)}>Budget</Text>
                        <Text style={th2(118)}>Actuals</Text>
                        <Text style={th2(118)}>% Rec'd / Prog.</Text>
                        <Text style={th2(118)}>Open</Text>
                        <Text style={th2(118)}>ETC</Text>
                        <Text style={th2(118)}>EAC</Text>
                        <Text style={th2(117)}>Variance</Text>
                    </View>
                    {watchItems.items.length === 0 && (
                        <View style={{ flexDirection: 'row', backgroundColor: COLORS.white }}>
                            <Text style={td2(1130, 'center')}>No open 3rd-party commitments for this job.</Text>
                        </View>
                    )}
                    {watchItems.items.map((it, i) => {
                        // Materials block (top 5) and Subs block (bottom 5) are each
                        // sorted by EAC descending on their own — mark the boundary
                        // between the two blocks so the grouping reads clearly.
                        const startsNewType = i > 0 && it.type !== watchItems.items[i - 1].type;
                        return (
                            <React.Fragment key={i}>
                                {startsNewType && (
                                    <View style={{ height: 1, backgroundColor: COLORS.subHeader }} />
                                )}
                                <View style={{
                                    flexDirection: 'row',
                                    backgroundColor: i % 2 === 0 ? COLORS.white : COLORS.zebra,
                                    borderTopWidth: startsNewType ? 0 : 1, borderColor: COLORS.grid,
                                }}>
                                    <Text numberOfLines={1} style={td2(250, 'left', true)}>
                                        {groupBy === 'resource'
                                            ? (it.desc || it.code)
                                            : (
                                                // Fixed-width code block so every description starts
                                                // at the same x regardless of digit widths.
                                                <>
                                                    <Text style={{ display: 'inline-block', minWidth: 52 }}>{it.code}</Text>
                                                    {it.desc ? '- ' + it.desc : ''}
                                                </>
                                            )}
                                    </Text>
                                    <Text style={td2(55, 'center')}>{it.type}</Text>
                                    <Text style={td2(118)}>{fK(it.budget)}</Text>
                                    <Text style={td2(118)}>{fK(it.actuals)}</Text>
                                    <Text style={td2(118)}>{fPct(it.pctReceived)}</Text>
                                    <Text style={td2(118)}>{fK(it.open)}</Text>
                                    <Text style={td2(118)}>{fK(it.etc)}</Text>
                                    <Text style={{ ...td2(118, 'right', true), color: COLORS.cellText }}>{fK(it.eac)}</Text>
                                    <Text style={{ ...td2(117, 'right', true), color: varColor(it.eacVar) }}>{fVar(it.eacVar)}</Text>
                                </View>
                            </React.Fragment>
                        );
                    })}
                </View>

                {/* ---------- Panel 2: Budget vs EAC by type ---------- */}
                <View style={{ height: 26 }} />
                <SectionTitle>Budget vs EAC by 3rd-Party Type</SectionTitle>

                {/* Summary table */}
                <View style={{ borderWidth: 1, borderColor: COLORS.grid, borderRadius: 4, overflow: 'hidden' }}>
                    <View style={{ flexDirection: 'row', backgroundColor: COLORS.navyDark }}>
                        <Text style={th2(170, 'left')}>Type</Text>
                        <Text style={th2(137)}>Budget</Text>
                        <Text style={th2(137)}>Actuals</Text>
                        <Text style={th2(137)}>% Rec'd / Prog.</Text>
                        <Text style={th2(137)}>Open</Text>
                        <Text style={th2(137)}>ETC</Text>
                        <Text style={th2(137)}>EAC</Text>
                        <Text style={th2(137)}>Variance</Text>
                    </View>
                    {[typeRows.materials, typeRows.subs, typeRows.total].map((row, i) => {
                        const isTotal = row.label === 'Total';
                        const color = isTotal ? COLORS.headerText : COLORS.cellText;
                        return (
                            <View key={row.label} style={{
                                flexDirection: 'row',
                                backgroundColor: isTotal ? COLORS.subHeader : (i % 2 === 0 ? COLORS.white : COLORS.zebra),
                                borderTopWidth: 1, borderColor: COLORS.grid,
                            }}>
                                <Text style={{ ...td2(170, 'left', true), color }}>{row.label}</Text>
                                <Text style={{ ...td2(137), color }}>{fK(row.budget)}</Text>
                                <Text style={{ ...td2(137), color }}>{fK(row.actuals)}</Text>
                                <Text style={{ ...td2(137), color }}>{fPct(row.pctReceived)}</Text>
                                <Text style={{ ...td2(137), color }}>{fK(row.open)}</Text>
                                <Text style={{ ...td2(137), color }}>{fK(row.etc)}</Text>
                                <Text style={{ ...td2(137), color }}>{fK(row.eac)}</Text>
                                <Text style={{ ...td2(137, 'right', true), color: isTotal ? COLORS.headerText : varColor(row.eacVar) }}>{fVar(row.eacVar)}</Text>
                            </View>
                        );
                    })}
                </View>

                <View style={{ height: 26 }} />
                <SectionTitle>3rd-Party Type</SectionTitle>

                {/* Bar chart — one bar per type, always spanning the full available
                    width (dollar totals don't affect bar length). The filled
                    portion is % Rec'd/Prog.; when EAC exceeds Budget, the overage
                    past the budget tick is filled red (the Variance). The budget
                    tick sits above the fill/variance layers so it's never hidden. */}
                {[typeRows.materials, typeRows.subs].map((row) => {
                    const isMat = row.label === 'Materials';
                    const fillColor = isMat ? COLORS.budgetBar : COLORS.eacBar;
                    const budget = pNum(row.budget);
                    const eac = pNum(row.eac);
                    const barTotal = Math.max(budget, eac, 1);
                    const budgetPosPct = Math.max(0, Math.min(100, (budget / barTotal) * 100));
                    const overBudget = eac > budget;
                    const fillPct = Math.max(0, Math.min(100, row.pctReceived * (eac / barTotal) * 100));
                    return (
                        <View key={row.label} style={{ flexDirection: 'row', alignItems: 'flex-end', paddingTop: 24, marginBottom: 10 }}>
                            <Text style={{ width: 90, fontSize: 13, fontWeight: '700', color: COLORS.navy, marginBottom: 6 }}>{row.label}</Text>
                            <View style={{ flex: 1, position: 'relative' }}>
                                <View style={{
                                    width: '100%', height: 32, borderWidth: 2, borderStyle: 'solid', borderColor: COLORS.barOutline, borderRadius: 2,
                                    overflow: 'hidden', backgroundColor: COLORS.white, position: 'relative',
                                }}>
                                    {overBudget && (
                                        <View style={{
                                            position: 'absolute', left: budgetPosPct + '%', top: 0, bottom: 0, right: 0,
                                            backgroundColor: COLORS.over,
                                        }} />
                                    )}
                                    <View style={{
                                        position: 'absolute', left: 0, top: 0, bottom: 0, width: fillPct + '%',
                                        backgroundColor: fillColor, justifyContent: 'center', alignItems: 'flex-end', paddingRight: 8,
                                    }}>
                                        {fillPct >= 14 && (
                                            <Text style={{ fontSize: 12, fontWeight: '700', color: '#fff' }}>{fPct(row.pctReceived)}</Text>
                                        )}
                                    </View>
                                </View>
                                {/* Budget tick + label, rendered after (so it paints on top of) the bar's fill/variance layers */}
                                <View style={{ position: 'absolute', left: budgetPosPct + '%', top: -18, alignItems: 'center', transform: 'translateX(-50%)', zIndex: 2 }}>
                                    <Text style={{ fontSize: 11, fontWeight: '700', color: COLORS.cellText, marginBottom: 2 }}>{fK(row.budget)}</Text>
                                    <View style={{ width: 2, height: 40, backgroundColor: COLORS.cellText }} />
                                </View>
                            </View>
                            <Text style={{ width: 70, fontSize: 12, fontWeight: '700', color: COLORS.cellText, textAlign: 'right', marginLeft: 10 }}>{fK(row.eac)}</Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

export default ThirdPartyCostDrivers;
